import {
  format,
  isSameYear,
  isThisYear,
  isToday,
  isYesterday,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';

export const resolveIntlLocale = locale => {
  if (!locale) return 'en';

  const normalizedLocale = locale.replace(/_/g, '-');
  const supportedLocales = Intl.DateTimeFormat.supportedLocalesOf([
    normalizedLocale,
  ]);

  if (supportedLocales.length) {
    return normalizedLocale;
  }

  const baseLocale = normalizedLocale.split('-')[0];
  const supportedBaseLocales = Intl.DateTimeFormat.supportedLocalesOf([
    baseLocale,
  ]);

  return supportedBaseLocales.length ? baseLocale : 'en';
};

const toUnixDate = time => {
  const date = fromUnixTime(Number(time));
  return Number.isNaN(date.getTime()) ? null : date;
};

export const regionalDateFormat = (time, locale, options = {}) => {
  const unixTime = toUnixDate(time);
  if (!unixTime) return '';

  return new Intl.DateTimeFormat(resolveIntlLocale(locale), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...options,
  }).format(unixTime);
};

export const regionalMessageTimestamp = (time, locale, options = {}) => {
  const { now = Date.now() } = options;
  const unixTime = toUnixDate(time);
  if (!unixTime) return '';

  const shouldShowYear = !isSameYear(unixTime, new Date(now));

  return regionalDateFormat(time, locale, {
    year: shouldShowYear ? 'numeric' : undefined,
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const regionalRelativeTime = (time, locale, options = {}) => {
  const { now = Date.now(), style = 'narrow' } = options;
  const unixTime = toUnixDate(time);
  if (!unixTime) return '';

  const diffInSeconds = Math.round((unixTime.getTime() - now) / 1000);
  const absDiffInSeconds = Math.abs(diffInSeconds);
  const formatter = new Intl.RelativeTimeFormat(resolveIntlLocale(locale), {
    numeric: 'auto',
    style,
  });

  if (absDiffInSeconds < 60) {
    return formatter.format(diffInSeconds, 'second');
  }

  if (absDiffInSeconds < 3600) {
    return formatter.format(Math.round(diffInSeconds / 60), 'minute');
  }

  if (absDiffInSeconds < 86400) {
    return formatter.format(Math.round(diffInSeconds / 3600), 'hour');
  }

  if (absDiffInSeconds < 2592000) {
    return formatter.format(Math.round(diffInSeconds / 86400), 'day');
  }

  if (absDiffInSeconds < 31536000) {
    return formatter.format(Math.round(diffInSeconds / 2592000), 'month');
  }

  return formatter.format(Math.round(diffInSeconds / 31536000), 'year');
};

const SHORT_RELATIVE_TIME_UNITS = {
  en: {
    now: 'now',
    minute: 'm',
    hour: 'h',
    day: 'd',
    month: count => (count === 1 ? 'mo' : 'mos'),
    year: 'y',
  },
  pt: {
    now: 'agora',
    minute: 'min',
    hour: 'h',
    day: 'd',
    month: count => (count === 1 ? 'mês' : 'meses'),
    year: 'a',
  },
  'pt-BR': {
    now: 'agora',
    minute: 'min',
    hour: 'h',
    day: 'd',
    month: count => (count === 1 ? 'mês' : 'meses'),
    year: 'a',
  },
  es: {
    now: 'ahora',
    minute: 'min',
    hour: 'h',
    day: 'd',
    month: count => (count === 1 ? 'mes' : 'meses'),
    year: 'a',
  },
  fr: {
    now: 'maint.',
    minute: 'min',
    hour: 'h',
    day: 'j',
    month: () => 'mois',
    year: 'a',
  },
  it: {
    now: 'ora',
    minute: 'min',
    hour: 'h',
    day: 'g',
    month: count => (count === 1 ? 'mese' : 'mesi'),
    year: 'a',
  },
  de: {
    now: 'jetzt',
    minute: 'min',
    hour: 'Std',
    day: 'T',
    month: count => (count === 1 ? 'Mon' : 'Mon'),
    year: 'J',
  },
};

const shortRelativeTimeUnits = locale => {
  const resolvedLocale = resolveIntlLocale(locale);
  return (
    SHORT_RELATIVE_TIME_UNITS[resolvedLocale] ||
    SHORT_RELATIVE_TIME_UNITS[resolvedLocale.split('-')[0]] ||
    SHORT_RELATIVE_TIME_UNITS.en
  );
};

export const regionalShortRelativeTime = (time, locale, options = {}) => {
  const { now = Date.now() } = options;
  const unixTime = toUnixDate(time);
  if (!unixTime) return '';

  const diffInSeconds = Math.round((unixTime.getTime() - now) / 1000);
  const absDiffInSeconds = Math.abs(diffInSeconds);
  const units = shortRelativeTimeUnits(locale);

  if (absDiffInSeconds < 60) {
    return units.now;
  }

  if (absDiffInSeconds < 3600) {
    return `${Math.round(absDiffInSeconds / 60)}${units.minute}`;
  }

  if (absDiffInSeconds < 86400) {
    return `${Math.round(absDiffInSeconds / 3600)}${units.hour}`;
  }

  if (absDiffInSeconds < 2592000) {
    return `${Math.round(absDiffInSeconds / 86400)}${units.day}`;
  }

  if (absDiffInSeconds < 31536000) {
    const monthCount = Math.round(absDiffInSeconds / 2592000);
    return `${monthCount}${units.month(monthCount)}`;
  }

  return `${Math.round(absDiffInSeconds / 31536000)}${units.year}`;
};

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat);
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy') => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const messageDate = format(messageTime, dateFormat);
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, 'LLL d y, h:mm a');
  }
  return messageDate;
};

/**
 * Formats a Unix timestamp relative to today: the time for today, a caller-
 * supplied label for yesterday, and a date otherwise. The yesterday label is
 * passed in so the caller keeps ownership of translation.
 * @param {number} time - Unix timestamp.
 * @param {string} yesterdayLabel - Localized label shown for yesterday.
 * @returns {string} Formatted timestamp string.
 */
export const relativeDayTimestamp = (time, yesterdayLabel) => {
  const date = fromUnixTime(time);
  if (isToday(date)) return format(date, 'h:mm a');
  if (isYesterday(date)) return yesterdayLabel;
  if (isThisYear(date)) return format(date, 'MMM d');
  return format(date, 'MMM d, yyyy');
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @param {string} [locale] - Optional locale for translated relative time.
 * @param {object} [options] - Options forwarded to regional relative time.
 * @returns {string} Relative time string.
 */
export const dynamicTime = (time, locale, options = {}) => {
  if (locale) {
    return regionalRelativeTime(time, locale, { style: 'long', ...options });
  }

  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, { addSuffix: true });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df);
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const timeMappings = {
    'less than a minute ago': 'now',
    'in less than a minute': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

/**
 * Formats a duration in seconds into mm:ss or hh:mm:ss.
 * @param {number|string} durationInSeconds - Duration in seconds.
 * @returns {string} Formatted duration string. Empty string for invalid input.
 */
export const formatDuration = durationInSeconds => {
  if (durationInSeconds === null || durationInSeconds === undefined) return '';

  const totalSeconds = Number(durationInSeconds);
  if (Number.isNaN(totalSeconds) || totalSeconds < 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
